/**
 * Field-side room layout — Phase 1B-I (2026-05-29 overhaul).
 *
 * Replaces the operator-centric RoomLayout for the field user. WhatsApp-
 * style composition:
 *
 *   • Local back camera fills the entire viewport (object-cover) — the
 *     technician's own view is the canvas, because that's what the
 *     operator is asking them to point at things.
 *   • Operator + any observers/auditors appear as a draggable
 *     glassmorphism PiP card in the top-right corner. Drag anywhere on
 *     the card to reposition; it snaps to the nearest viewport edge on
 *     release. Tap to cycle between remote participants when there are
 *     more than one.
 *   • Floating bottom-center toolbar (rounded pill, glass) hosts the
 *     field-controllable hardware: mic, camera flip, torch, capture.
 *     Chat lives in a separate bottom-right FAB (existing
 *     FieldChatDrawer mounted by FieldRoom) so the toolbar stays focused
 *     on hardware controls.
 *
 * Why local-camera-as-backdrop:
 *   The field is the inspection subject. Showing them their own camera
 *   full-resolution lets them frame defects without squinting at a
 *   thumbnail. The operator's face is useful for emotional context but
 *   doesn't need to dominate the viewport — hence PiP.
 *
 * Annotation overlay: the local camera tile is marked with
 * `data-cvp-role="dominant-tile"` so the existing InRoomAnnotation /
 * AnnotationOverlay code paths find the right video element and draw
 * the operator's marks over the right frame.
 *
 * Sounds: capture button plays the camera-shutter chime; mic / camera
 * flip play a soft tap. Real CC0 assets land in a follow-up commit; for
 * now we synthesise a short click via the Web Audio API as a
 * sound-effect placeholder (so the UX feels right today, asset swap is
 * a one-line change in soundEffects.ts).
 */
import {
  ParticipantTile,
  TrackRefContext,
  useLocalParticipant,
  useRoomContext,
  useTracks,
} from "@livekit/components-react";
import { isTrackReference } from "@livekit/components-core";
import { LocalVideoTrack, Track } from "livekit-client";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  applyTorch,
  switchFacing,
  upgradeCaptureResolution,
  type FacingMode,
} from "@/lib/cameraCommands";
import { useCameraIntent } from "@/lib/cameraIntent";
import { useConnectionQualityStore } from "@/lib/connectionQuality";
import {
  emitCrossSideSound,
  useCrossSideSounds,
} from "@/lib/crossSideSounds";
import { captureFrameAsBlob, uploadFieldSnapshot } from "@/lib/evidence";
import { acquireGeolocation, postFieldSessionGeo } from "@/lib/geolocation";
import { playSound } from "@/lib/soundEffects";

type Props = {
  sessionId: string;
  fieldSessionToken: string;
};

export function FieldLayout({ sessionId, fieldSessionToken }: Props) {
  const { t } = useTranslation();
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  const cameraTracks = useMemo(
    () => tracks.filter((t) => t.source === Track.Source.Camera),
    [tracks],
  );

  // Remote screen-share (the operator presenting something to the field).
  // When present it OWNS the field's main view so the technician can
  // actually see what's being shared — previously the field only ever
  // rendered camera tracks, so an operator screen-share was invisible on
  // the field/mobile side.
  const remoteScreenShare = useMemo(
    () =>
      tracks
        .filter(
          (t) =>
            t.source === Track.Source.ScreenShare && !t.participant.isLocal,
        )
        .find(isTrackReference) ?? null,
    [tracks],
  );

  const localTrack =
    cameraTracks.find((t) => t.participant.isLocal) ?? null;
  const remoteTracks = useMemo(
    () =>
      cameraTracks
        .filter((t) => !t.participant.isLocal)
        .filter(isTrackReference),
    [cameraTracks],
  );

  // The PiP normally stacks the remote cameras. When a screen-share owns
  // the main view, the field's own camera joins the PiP so they still see
  // themselves (and the operator's camera, if on, stays alongside).
  const pipTracks = useMemo(() => {
    if (remoteScreenShare && localTrack && isTrackReference(localTrack)) {
      return [localTrack, ...remoteTracks];
    }
    return remoteTracks;
  }, [remoteScreenShare, localTrack, remoteTracks]);

  // Auto-enable camera + mic once on mount. Mirrors RoomLayout's logic —
  // <LiveKitRoom video audio> is documented to do this automatically but
  // mobile Safari can fail silently on first connect.
  const initRef = useRef(false);
  useEffect(() => {
    if (!localParticipant || initRef.current) return;
    initRef.current = true;
    void localParticipant.setCameraEnabled(true).catch(() => {});
    void localParticipant.setMicrophoneEnabled(true).catch(() => {});
  }, [localParticipant]);

  // Standalone in-room GPS acquire (robustness #1). The consent screen
  // already tries once before join, but a cold mobile GPS can still be
  // warming up then — and if the field never takes a capture, that lone
  // attempt is the ONLY shot at a session location. So we retry here, once
  // the tech is live in the room (GPS has had time to lock), and write it
  // to session.geo_jsonb. Best-effort: geo is proof-enhancing, not required.
  const geoRef = useRef(false);
  useEffect(() => {
    if (geoRef.current) return;
    geoRef.current = true;
    void (async () => {
      const outcome = await acquireGeolocation({ retries: 2 });
      if (!outcome.ok) return;
      try {
        await postFieldSessionGeo(sessionId, fieldSessionToken, outcome.fix);
      } catch {
        /* Geo write is non-essential. */
      }
    })();
  }, [sessionId, fieldSessionToken]);

  // Phase 1B-I — the PiP now stacks EVERY remote (operator + every
  // observer / auditor / additional field) so the field user sees who
  // they're talking to at a glance. Previously the PiP cycled through
  // remotes one at a time on tap; the cycle UX hid the fact that
  // multiple people were watching, which surprised field users on
  // multi-party inspections.

  // Hardware state shadows — needed so the toolbar buttons can render
  // the right icon/label without re-querying MediaStreamTrack each
  // render.
  const [micOn, setMicOn] = useState(true);
  const [facing, setFacing] = useState<FacingMode>("environment");
  const [torchOn, setTorchOn] = useState(false);
  const [torchSupported, setTorchSupported] = useState(false);
  const [busy, setBusy] = useState<null | "flip" | "torch" | "capture">(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  // Probe torch support whenever the local video track changes (new
  // publication on first mount or after a camera switch).
  useEffect(() => {
    const pub = localParticipant?.getTrackPublication(Track.Source.Camera);
    const mst = pub?.track?.mediaStreamTrack;
    if (!mst) return;
    const caps = mst.getCapabilities
      ? (mst.getCapabilities() as MediaTrackCapabilities & { torch?: boolean })
      : ({} as { torch?: boolean });
    setTorchSupported(!!caps.torch);
  }, [localParticipant, localTrack?.publication?.trackSid]);

  const handleMicToggle = useCallback(() => {
    if (!localParticipant) return;
    const next = !micOn;
    void localParticipant.setMicrophoneEnabled(next).catch(() => {});
    setMicOn(next);
    void playSound("tap");
  }, [localParticipant, micOn]);

  const handleFlip = useCallback(async () => {
    if (!localParticipant || busy) return;
    setBusy("flip");
    setError(null);
    void playSound("tap");
    // Tell the shared intent a deliberate switch is in flight so
    // FieldCommandsHandler's auto-restore doesn't fight (and then revert)
    // the field tech's own flip — the "same camera again and again" bug.
    const intent = useCameraIntent.getState();
    intent.setSwitching(true);
    try {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const track = pub?.track;
      if (!(track instanceof LocalVideoTrack)) {
        throw new Error(t("ui.fieldLayout.cameraNotReady"));
      }
      const next: FacingMode = facing === "environment" ? "user" : "environment";
      const applied = await switchFacing(track, next);
      // Record the field's choice as the new desired facing so the
      // auto-restore keeps THIS facing, not the operator's last pick.
      intent.setDesired(applied);
      // switchFacing's applyConstraints path sends only {facingMode}, which
      // drops width/height → the camera falls back to its default (480×640
      // on iPhone). Re-apply HD, exactly as the operator-driven switch does
      // in FieldCommandsHandler. Without this the field tech's own flip
      // silently degrades the published resolution.
      await upgradeCaptureResolution(track);
      setFacing(applied);
      // Torch resets on any track replacement.
      setTorchOn(false);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
      // Grace period mirrors the operator-switch path — Track.Restarted can
      // fire shortly after the await returns.
      setTimeout(() => intent.setSwitching(false), 500);
    }
  }, [localParticipant, facing, busy, t]);

  const handleTorch = useCallback(async () => {
    if (!localParticipant || busy) return;
    setBusy("torch");
    setError(null);
    void playSound("tap");
    try {
      const pub = localParticipant.getTrackPublication(Track.Source.Camera);
      const mst = pub?.track?.mediaStreamTrack;
      if (!mst) throw new Error(t("ui.fieldLayout.cameraNotReady"));
      const next = !torchOn;
      await applyTorch(mst, next);
      setTorchOn(next);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [localParticipant, torchOn, busy, t]);

  // Cross-side sounds: listen for operator-triggered shutter etc.
  useCrossSideSounds();

  const handleCapture = useCallback(async () => {
    if (busy) return;
    setBusy("capture");
    setError(null);
    // Plays locally + broadcasts to operator so they hear the shutter too.
    void emitCrossSideSound("shutter");
    try {
      const video = document.querySelector<HTMLVideoElement>(
        'video[data-lk-source="camera"][data-lk-local-participant="true"]',
      );
      if (!video) throw new Error(t("ui.fieldLayout.noLocalCamera"));
      // Same tier the live capture resolution adapts to — see
      // FieldCommandsHandler's ConnectionQualityChanged listener.
      const tier = useConnectionQualityStore.getState().tier;
      const frame = await captureFrameAsBlob(
        video,
        "image/jpeg",
        tier === "low" ? 0.65 : 0.85,
      );
      // Capture-time GPS — consent screen primed the permission.
      const geo = await acquireGeolocation();
      await uploadFieldSnapshot({
        sessionId,
        frame,
        fieldSessionToken,
        geo: geo.ok
          ? {
              lat: geo.fix.lat,
              lng: geo.fix.lng,
              accuracy_m: geo.fix.accuracy_m,
              source: geo.fix.source,
              timestamp: geo.fix.timestamp,
            }
          : { source: "unknown" },
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  }, [sessionId, fieldSessionToken, busy, t]);
  void room; // avoid unused warning; useRoomContext call asserts context

  return (
    <div className="relative h-full overflow-hidden bg-black">
      {/* Main view. Normally the local camera fills the viewport
          (object-cover so the frame fills a phone — the WhatsApp/camera
          UX users expect). When the operator is screen-sharing, THAT
          becomes the main view instead, with object-contain so the
          whole shared screen is legible (cover would crop it). Either
          way it carries data-cvp-role="dominant-tile" so the annotation
          selector + InRoomAnnotation bind to whatever is on screen. */}
      {remoteScreenShare ? (
        <div
          className="absolute inset-0 bg-black [&_video]:!object-contain"
          data-cvp-role="dominant-tile"
        >
          <TrackRefContext.Provider value={remoteScreenShare}>
            <ParticipantTile
              trackRef={remoteScreenShare}
              className="h-full w-full"
            />
          </TrackRefContext.Provider>
        </div>
      ) : (
        <div
          className="absolute inset-0 [&_video]:!object-cover"
          data-cvp-role="dominant-tile"
        >
          {localTrack ? (
            <TrackRefContext.Provider value={localTrack}>
              <ParticipantTile trackRef={localTrack} className="h-full w-full" />
            </TrackRefContext.Provider>
          ) : (
            <FieldPlaceholder />
          )}
        </div>
      )}

      {pipTracks.length > 0 && (
        <DraggablePiP remoteCount={pipTracks.length}>
          {pipTracks.map((track) => (
            <div
              key={`${track.participant.identity}-${track.publication?.trackSid ?? "no-pub"}`}
              className="relative min-h-0 flex-1 overflow-hidden [&_video]:!object-cover"
            >
              <TrackRefContext.Provider value={track}>
                <ParticipantTile
                  trackRef={track}
                  className="h-full w-full"
                />
              </TrackRefContext.Provider>
            </div>
          ))}
        </DraggablePiP>
      )}

      <FieldToolbar
        micOn={micOn}
        torchOn={torchOn}
        torchSupported={torchSupported}
        busy={busy}
        onMic={handleMicToggle}
        onFlip={handleFlip}
        onTorch={handleTorch}
        onCapture={handleCapture}
      />

      {error && (
        <div
          role="alert"
          className="pointer-events-none absolute left-1/2 top-6 -translate-x-1/2 rounded-md bg-cvp-danger/85 px-3 py-1.5 text-xs text-white shadow-lg backdrop-blur"
        >
          {error}
        </div>
      )}
    </div>
  );
}

function FieldPlaceholder() {
  const { t } = useTranslation();
  return (
    <div className="flex h-full items-center justify-center text-sm text-nb-text-2">
      {t("ui.fieldLayout.startingCamera")}
    </div>
  );
}

/**
 * Draggable, corner-snapping PiP. Glassmorphism card containing a vertical
 * stack of every remote participant's video.
 *
 * Pointer events:
 *   • pointerdown → start drag; capture pointer.
 *   • pointermove → update transform (free 2-D dragging anywhere).
 *   • pointerup → release; snap to the nearest of the four corners,
 *     clamped to the viewport-safe-area.
 *
 * Sizing: width is fixed (a single column); height grows with the
 * number of remotes — each tile gets ~104px, capped at 4 tiles before
 * the inner area becomes scrollable. The cap keeps the PiP from
 * monopolising the viewport on a many-party room (operator + several
 * observers); the field can still drag the card around to see what
 * it's covering.
 *
 * Stack-mode badge: when there's more than one remote, the count
 * appears in the top-right of the card so the field knows the
 * inspection has additional viewers even if a tile is scrolled out.
 */
const PIP_WIDTH_PX = 120;
const PIP_TILE_HEIGHT_PX = 104;
const PIP_MAX_VISIBLE_TILES = 4;

function DraggablePiP({
  children,
  remoteCount,
}: {
  children: React.ReactNode;
  remoteCount: number;
}) {
  // Default position: top-right corner with safe-area inset.
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  const cardRef = useRef<HTMLDivElement | null>(null);

  // Height grows with N up to the cap; beyond, scroll inside.
  const visibleTiles = Math.min(remoteCount, PIP_MAX_VISIBLE_TILES);
  const cardHeight = Math.max(1, visibleTiles) * PIP_TILE_HEIGHT_PX;

  // Initialise position to top-right on mount. Use window dimensions
  // because the wrapper element may not have measured yet.
  useEffect(() => {
    if (pos) return;
    const card = cardRef.current;
    if (!card) return;
    const cw = card.offsetWidth || PIP_WIDTH_PX;
    const x = (window.innerWidth || 0) - cw - 16;
    const y = 16;
    setPos({ x, y });
  }, [pos]);

  // Re-clamp when the card resizes (a remote joins or leaves and we
  // grow / shrink the height) so it doesn't push off the bottom edge.
  useEffect(() => {
    const card = cardRef.current;
    if (!card || !pos) return;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const nextX = Math.max(8, Math.min(pos.x, vw - cw - 8));
    const nextY = Math.max(8, Math.min(pos.y, vh - ch - 96));
    if (nextX !== pos.x || nextY !== pos.y) setPos({ x: nextX, y: nextY });
    // pos intentionally NOT in deps — we only want to re-clamp on
    // height changes (visibleTiles delta), not after our own setPos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTiles]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!pos) return;
    const card = cardRef.current;
    if (!card) return;
    card.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
    };
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.moved && Math.hypot(dx, dy) > 5) drag.moved = true;
    setPos({ x: drag.origX + dx, y: drag.origY + dy });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    dragRef.current = null;
    const card = cardRef.current;
    if (card?.hasPointerCapture(e.pointerId)) {
      card.releasePointerCapture(e.pointerId);
    }
    if (!drag) return;
    if (!card) return;
    // Snap to the nearest of the four corners (FaceTime-style). Free
    // dragging while held, predictable resting spots on release — and
    // the corners keep the PiP clear of the centre of frame where the
    // inspection subject is. The bottom inset leaves room for the
    // field toolbar pill so a bottom-corner drop doesn't hide under it.
    const cw = card.offsetWidth;
    const ch = card.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 16;
    const bottomInset = 96;
    const cx = (pos?.x ?? 0) + cw / 2;
    const cy = (pos?.y ?? 0) + ch / 2;
    const snapX = cx < vw / 2 ? margin : vw - cw - margin;
    const snapY = cy < vh / 2 ? margin : vh - ch - bottomInset;
    setPos({ x: snapX, y: snapY });
  };

  // Re-clamp on viewport resize so a rotation doesn't strand the PiP
  // off-screen.
  useEffect(() => {
    const onResize = () => {
      const card = cardRef.current;
      if (!card || !pos) return;
      const cw = card.offsetWidth;
      const ch = card.offsetHeight;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      setPos({
        x: Math.max(8, Math.min(pos.x, vw - cw - 8)),
        y: Math.max(8, Math.min(pos.y, vh - ch - 96)),
      });
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [pos]);

  return (
    <div
      ref={cardRef}
      data-testid="field-pip"
      data-cvp-role="pip-card"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      style={{
        transform: pos ? `translate(${pos.x}px, ${pos.y}px)` : undefined,
        visibility: pos ? "visible" : "hidden",
        touchAction: "none",
        width: `${PIP_WIDTH_PX}px`,
        height: `${cardHeight}px`,
      }}
      className="absolute left-0 top-0 z-30 cursor-grab overflow-hidden rounded-2xl border border-white/15 bg-black/40 shadow-2xl backdrop-blur-md active:cursor-grabbing"
    >
      {/* Vertical stack of all remote tiles. Each tile fills the full
          width and an equal share of the card height. When the count
          exceeds PIP_MAX_VISIBLE_TILES, this inner area becomes
          scrollable so additional participants are still reachable. */}
      <div
        className="pointer-events-none flex h-full flex-col gap-px overflow-y-auto"
        // The tiles are `pointer-events-none` so every touch/pointer
        // lands on the draggable card itself — not the LiveKit <video>
        // underneath, which would otherwise claim the gesture and let
        // the browser treat it as a scroll (the old "PiP only nudges up
        // and down, won't drag sideways" bug). The card's
        // touchAction:none then guarantees free 2-D dragging.
      >
        {children}
      </div>
      {remoteCount > 1 && (
        <span className="pointer-events-none absolute right-1 top-1 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] text-white">
          {remoteCount}
        </span>
      )}
    </div>
  );
}

/**
 * Bottom-center floating toolbar. Rounded pill with glass background +
 * mint accent. Buttons are 48×48 touch targets, large enough for a
 * gloved hand on a phone.
 */
function FieldToolbar({
  micOn,
  torchOn,
  torchSupported,
  busy,
  onMic,
  onFlip,
  onTorch,
  onCapture,
}: {
  micOn: boolean;
  torchOn: boolean;
  torchSupported: boolean;
  busy: null | "flip" | "torch" | "capture";
  onMic: () => void;
  onFlip: () => void;
  onTorch: () => void;
  onCapture: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="field-toolbar"
      data-cvp-role="field-toolbar"
      className="pointer-events-auto absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full border border-white/15 bg-black/55 px-3 py-2 shadow-2xl backdrop-blur-md"
    >
      <ToolButton
        active={!micOn}
        onClick={onMic}
        title={micOn ? t("ui.fieldLayout.mute") : t("ui.fieldLayout.unmute")}
        data-testid="field-mic-toggle"
      >
        {micOn ? <MicIcon /> : <MicOffIcon />}
      </ToolButton>

      <ToolButton
        onClick={onFlip}
        disabled={busy === "flip"}
        title={t("ui.fieldLayout.flipCamera")}
        data-testid="field-camera-flip"
      >
        <FlipIcon />
      </ToolButton>

      <ToolButton
        emphasis
        onClick={onCapture}
        disabled={busy === "capture"}
        title={t("ui.fieldLayout.capture")}
        data-testid="field-capture"
      >
        <ShutterIcon />
      </ToolButton>

      <ToolButton
        active={torchOn}
        onClick={onTorch}
        disabled={!torchSupported || busy === "torch"}
        title={t("ui.fieldLayout.torch")}
        data-testid="field-torch-toggle"
      >
        <TorchIcon />
      </ToolButton>
    </div>
  );
}

function ToolButton({
  children,
  active,
  emphasis,
  title,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  active?: boolean;
  emphasis?: boolean;
}) {
  // Same hover treatment as the operator toolbar — `group` wrapper
  // drives both a subtle icon shrink and a tiny floating label that
  // appears below the button so the operator/field doesn't need to
  // memorise the icon row. Label sits ABOVE the toolbar (mt-1.5 +
  // top-full would clash with the toolbar's bottom edge on mobile, so
  // we position it ABOVE — `bottom-full` + `mb-1.5`).
  const base =
    "flex h-12 w-12 items-center justify-center rounded-full transition disabled:cursor-not-allowed disabled:opacity-40 [&_svg]:transition-transform [&_svg]:duration-150 group-hover:[&_svg]:scale-90";
  const palette = emphasis
    ? "bg-nb-mint text-nb-bg hover:bg-nb-mint/90"
    : active
      ? "bg-nb-mint/85 text-nb-bg hover:bg-nb-mint"
      : "bg-white/10 text-white hover:bg-white/20";
  return (
    <span className="group relative inline-flex">
      <button
        type="button"
        className={`${base} ${palette}`}
        aria-label={title ?? undefined}
        {...rest}
      >
        {children}
      </button>
      {title && (
        <span
          role="tooltip"
          className="pointer-events-none absolute bottom-full left-1/2 z-40 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md bg-black/90 px-2 py-0.5 text-[10px] font-medium text-white opacity-0 shadow-md transition-opacity duration-150 group-hover:opacity-100"
        >
          {title}
        </span>
      )}
    </span>
  );
}

// --- Icons — inline SVG so we don't pull a new dependency. -----------

function MicIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M12 14a3 3 0 003-3V6a3 3 0 00-6 0v5a3 3 0 003 3z"
        stroke="currentColor"
        strokeWidth="2"
      />
      <path
        d="M19 11a7 7 0 01-14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
function MicOffIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M3 3l18 18M9 6.4V6a3 3 0 016 0v5c0 .35-.06.68-.17 1M15 14.2a3 3 0 01-5.81-1.06M19 11a7 7 0 01-1.05 3.67M5 11a7 7 0 003.32 5.93M12 18v3"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}
function FlipIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M3 7h13l-2-2M3 7l2-2M21 17H8l2 2M21 17l-2 2"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ShutterIcon() {
  // Camera body + lens — clearly "take a photo" instead of the
  // ambiguous record-button shape (filled circle inside a ring).
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6" aria-hidden>
      <path
        d="M3 8a2 2 0 012-2h2.5l1.5-2h6l1.5 2H19a2 2 0 012 2v10a2 2 0 01-2 2H5a2 2 0 01-2-2V8z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
      />
      <circle
        cx="12"
        cy="13"
        r="3.5"
        stroke="currentColor"
        strokeWidth="2"
      />
    </svg>
  );
}
function TorchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" className="h-5 w-5" aria-hidden>
      <path
        d="M9 3h6l-1 4h2l-5 14 1-9H8l1-9z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}
